import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';

import SearchInput from '../../../components/common-inputs/SearchInput';
import WithLabel from '../../../components/with-label/WithLabel';
import CommonDropdown from '../../../components/common-dropdown/CommonDropdown';
import { IMAGES_PAGE_SIZE_OPTIONS } from '../../../id8/components/frames/filters/FrameFilters';
import useActiv8Filters from '../../hooks/useActiv8Filters';
import CommonFilterButton from '../../../components/common-filter-button/CommonFilterButton';
import CommonFiltersModal from '../../../components/common-filters-modal/CommonFiltersModal';
import CommonFilterChips from '../../../components/common-filter-chips/CommonFilterChips';
import UploadButton from '../../components/upload-button/UploadButton';
import { changeTableSettings } from '../../../redux/activ8/activ8Reducer';
import useCommonModal from '../../../hooks/useCommonModal';
import { useProject } from '../../../context/ProjectContext';
import SortDropdown from '../../../components/sort-dropdown/SortDropdown';
import removeDash from '../../../utils/removeDash';
import { PROJECT_VARIANTS, SORT_ORDER } from '../../../constants';
import useSortOptions from '../../hooks/useSortOptions';
import AdditionalFilters from './additional-filters/AdditionalFilters';
import WithError from '../../../components/with-error/WithError';
import { MIN_SEARCH_QUERY_LENGTH } from '../../../redux/activ8/activ8Actions';

import scss from './FindFilters.module.scss';

function FindFilters() {
  const dispatch = useDispatch();
  const { t } = useTranslation('activ8');
  const { id } = useParams();

  const { actions, projectVariant } = useProject();
  const sortOptions = useSortOptions();

  const { searchQuery, size, filters: filterSettings, page, sort } = useSelector((state) => state.activ8.tableSettings);
  const content = useSelector((state) => state.activ8.content);

  const {
    isOpen: isOpenFiltersModal,
    handleOpenModal: handleOpenFiltersModal,
    handleCloseModal: handleCloseFiltersModal,
  } = useCommonModal();

  const filters = useActiv8Filters();

  const trimmedSearchQuery = useMemo(() => searchQuery?.trim(), [searchQuery]);

  const handleSearch = (newValue) => {
    dispatch(changeTableSettings({ searchQuery: newValue, page: 0 }));
  };

  const handleChangeTableSize = (newValue) => {
    dispatch(changeTableSettings({ size: newValue, page: 0 }));
  };

  const handleChangeFilters = (newFilters) => {
    dispatch(changeTableSettings({ filters: newFilters, page: 0 }));
  };

  const handleRemoveFilter = (groupName, option) => {
    const groupItems = filterSettings?.[groupName] || [];
    const newState = { ...filterSettings };
    newState[groupName] = groupItems.filter((item) => item !== option);

    handleChangeFilters(newState);
  };

  const handleChangeSort = ({ sort, direction }) => {
    dispatch(changeTableSettings({ sort: `${direction === SORT_ORDER.DESC ? '-' : ''}${sort}` }));
  };

  useEffect(() => {
    actions('searchContent', { id });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, size, searchQuery, filterSettings, id, sort]);

  return (
    <div className={scss.wrapper}>
      <div className={scss.leftSection}>
        <WithError
          isShowError={trimmedSearchQuery.length && trimmedSearchQuery.length < MIN_SEARCH_QUERY_LENGTH}
          errorText={t('atLeastNumberCharacters', { number: MIN_SEARCH_QUERY_LENGTH })}
          extraClassName={scss.searchInputWrapper}
          isAbsolute>
          <SearchInput value={searchQuery} onChange={handleSearch} size='sm' name='activ8-search' />
        </WithError>

        <div className={scss.filtersWrapper}>
          <CommonFilterButton onOpen={handleOpenFiltersModal} selectedFilters={filterSettings} isShowCounter />
          <CommonFilterChips
            selectedFilters={filterSettings}
            onRemoveFilter={handleRemoveFilter}
            isShowGroupName
            t={t}
          />
        </div>
      </div>

      <div className={scss.rightSection}>
        {content.length && projectVariant === PROJECT_VARIANTS.ADMIN ? <UploadButton /> : null}

        <div className={scss.sortingWrapper}>
          <WithLabel label={t('sortBy')} maxWidth={208} isTwoDots>
            <SortDropdown
              options={sortOptions}
              sort={removeDash(sort)}
              direction={sort.includes('-') ? SORT_ORDER.DESC : SORT_ORDER.ASC}
              onChange={handleChangeSort}
              closeOnApply
            />
          </WithLabel>

          <WithLabel label={t('show')} maxWidth={168} isTwoDots>
            <CommonDropdown value={size} onSelect={handleChangeTableSize} options={IMAGES_PAGE_SIZE_OPTIONS} />
          </WithLabel>
        </div>
      </div>

      <CommonFiltersModal
        isOpen={isOpenFiltersModal}
        onClose={handleCloseFiltersModal}
        selectedFilters={filterSettings}
        filters={filters}
        onSelect={handleChangeFilters}
        AdditionalFilters={AdditionalFilters}
      />
    </div>
  );
}

export default FindFilters;
